import { CAPTURE_DEFAULTS, THRESHOLDS, TIMING } from '@erectus/shared';
import { PHONE_TILT } from '@erectus/shared';
import type {
  Calibration,
  FrameMetrics,
  FrameRecord,
  FrameState,
  GateResult,
  Landmark,
} from '@erectus/shared';
import { computeFrameMetrics, framingScore } from '@/metrics/index.js';
import { LandmarkSmoother, RollingMedian } from '@/pose/oneEuro.js';
import { qualityGate } from '@/pose/qualityGate.js';
import {
  detectOnVideo,
  loadLandmarker,
  releaseLandmarker,
  meanLuma,
  PoseModelLoadError,
  type LoadedLandmarker,
} from '@/pose/landmarker.js';
import { FrameStateMachine, rawFrameState } from '@/scoring/frameState.js';
import { ArtifactClassifier, FeatureWindow, applyClassifier } from '@/ml/classifier.js';
import { levelLandmarks } from '@/pose/level.js';
import { appendFrame, quantize } from './db.js';

/**
 * Session recorder (docs/PROMPTS.md Phase 2 §1).
 *
 * State machine: idle → setup → calibrating → running ⇄ paused → ending → done.
 *
 * Camera frames flow `<video>` → landmarker only. The frame buffer is never
 * copied out, never drawn to an exported canvas and never uploaded
 * (CLAUDE.md "하지 말 것"). The overlay is redrawn from landmark numbers.
 */

export type RecorderPhase =
  | 'idle'
  | 'setup'
  | 'calibrating'
  | 'running'
  | 'paused'
  | 'ending'
  | 'done';

/** Landmarker rebuilds in a row before giving up and telling the user. */
const MAX_TRACKING_RECOVERIES = 3;

export type PauseReason = 'user' | 'background' | 'multi_person' | 'gate_failed' | 'break';

/**
 * Pauses the recorder lifts by itself once the picture is measurable again.
 * A pause the user asked for ('user') or a rest break ('break') must not be:
 * auto-resuming those undid them instantly while the person was still seated.
 */
const AUTO_RESUMING: ReadonlySet<PauseReason> = new Set(['background', 'multi_person', 'gate_failed']);

export interface RecorderTick {
  phase: RecorderPhase;
  elapsedMs: number;
  state: FrameState;
  metrics: FrameMetrics;
  gate: GateResult;
  landmarks: readonly Landmark[];
  fps: number;
  frameCount: number;
  /** Seconds the gate has been failing continuously (banner after 30 s). */
  gateFailingSec: number;
  framing: number;
}

export interface RecorderOptions {
  sessionId: string;
  /** Used to decide classifier rollout membership; omit to skip the classifier. */
  uid?: string;
  video: HTMLVideoElement;
  plannedMin: number;
  sampleFps: number;
  poseModel: 'lite' | 'full';
  calibration?: Calibration | null;
  onTick(tick: RecorderTick): void;
  onPhase(phase: RecorderPhase, reason?: PauseReason): void;
  onError(error: RecorderError): void;
  /** Phone roll in the picture, degrees (lib/motion.ts); null/absent = no correction. */
  level?(): number | null;
  /** A landmarker rebuild finished (`ok`) or gave up. */
  onRecovery?(ok: boolean): void;
}

export interface RecorderError {
  code: 'camera_denied' | 'model_load' | 'gpu_fail' | 'lost_tracking' | 'storage_full';
  message: string;
}

export interface CalibrationResult {
  cvaBaseline: number | null;
  trunkBaseline: number | null;
  protBaseline: number | null;
  sampleCount: number;
}

export class SessionRecorder {
  private phase: RecorderPhase = 'idle';
  private loaded: LoadedLandmarker | null = null;
  /** Rebuilds of the landmarker since tracking last worked (see `recoverTracking`). */
  private recoveries = 0;
  private recovering = false;
  private lastCallbackAt = 0;
  private smoother = new LandmarkSmoother();
  private stateMachine = new FrameStateMachine();
  private cvaMedian = new RollingMedian(TIMING.medianWindowSec * 1000);
  private chaMedian = new RollingMedian(TIMING.medianWindowSec * 1000);
  private trunkMedian = new RollingMedian(TIMING.medianWindowSec * 1000);
  private protMedian = new RollingMedian(TIMING.medianWindowSec * 1000);

  private startedAt = 0;
  private pausedMs = 0;
  private pausedAt: number | null = null;
  private pauseReason: PauseReason | null = null;
  private frameCount = 0;
  private rafHandle: number | null = null;
  private videoFrameHandle: number | null = null;
  private timerHandle: number | null = null;
  private lastSampleAt = 0;
  private lastFrameAt = 0;
  private measuredFps = 0;
  private gateFailingSince: number | null = null;
  private framingSum = 0;
  private framingSamples = 0;
  private wakeLock: { release(): Promise<void> } | null = null;
  private calibrationSamples: FrameMetrics[] = [];
  private calibrating = false;
  private stopped = false;
  private classifier: ArtifactClassifier | null = null;
  private featureWindow = new FeatureWindow();
  private lastClassifiedAt = 0;
  private lastVerdictIsArtifact = false;
  private disagreements = 0;

  constructor(private readonly options: RecorderOptions) {}

  get currentPhase(): RecorderPhase {
    return this.phase;
  }

  get elapsedMs(): number {
    if (this.startedAt === 0) return 0;
    const pausedTotal = this.pausedMs + (this.pausedAt === null ? 0 : Date.now() - this.pausedAt);
    return Date.now() - this.startedAt - pausedTotal;
  }

  get framing(): number {
    return this.framingSamples === 0 ? 0 : this.framingSum / this.framingSamples;
  }

  /** Frames where the on-device classifier disagreed with the rules (Phase 8). */
  get disagreementCount(): number {
    return this.disagreements;
  }

  /**
   * A detect call threw. On iOS this is usually the WebGL context being
   * reclaimed after a long run, not anything about the picture — so rebuild the
   * landmarker (GPU, else CPU) and carry on. Frames meanwhile are simply not
   * sampled. Only when rebuilding keeps failing does the user see an error.
   */
  private async recoverTracking(): Promise<void> {
    if (this.recovering || this.stopped) return;
    this.recovering = true;
    this.loaded = null;
    this.recoveries += 1;
    try {
      if (this.recoveries > MAX_TRACKING_RECOVERIES) throw new Error('tracking keeps failing');
      releaseLandmarker();
      this.loaded = await loadLandmarker(this.options.poseModel);
      this.options.onRecovery?.(true);
    } catch {
      this.options.onRecovery?.(false);
      this.options.onError({
        code: 'lost_tracking',
        message: 'Pose tracking stopped and could not restart. Close this screen and open it again.',
      });
    } finally {
      this.recovering = false;
    }
  }

  /** Load the model and start the preview loop without recording anything yet. */
  async prepare(): Promise<void> {
    this.setPhase('setup');
    try {
      this.loaded = await loadLandmarker(this.options.poseModel);
    } catch (error) {
      const code = error instanceof PoseModelLoadError ? error.code : 'model_load';
      this.options.onError({
        code,
        message:
          error instanceof Error
            ? error.message
            : 'Could not load the posture model. Reload and try again.',
      });
      throw error;
    }
    // Optional and best-effort: a session must run with or without a classifier.
    if (this.options.uid) {
      this.classifier = await ArtifactClassifier.load(this.options.uid);
    }
    this.startLoop();
  }

  /** Collect a 5-second upright baseline (PRODUCT §2 screen 4). */
  async calibrate(durationMs = 5000): Promise<CalibrationResult> {
    this.setPhase('calibrating');
    this.calibrationSamples = [];
    this.calibrating = true;
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, durationMs);
    });
    this.calibrating = false;
    const median = (pick: (m: FrameMetrics) => number | null): number | null => {
      const values = this.calibrationSamples
        .map(pick)
        .filter((v): v is number => v !== null && Number.isFinite(v))
        .sort((a, b) => a - b);
      if (values.length === 0) return null;
      return values[Math.floor(values.length / 2)] ?? null;
    };
    const result: CalibrationResult = {
      cvaBaseline: median((m) => m.cva),
      trunkBaseline: median((m) => m.trunkInclination),
      protBaseline: median((m) => m.shoulderProtraction),
      sampleCount: this.calibrationSamples.length,
    };
    this.setPhase('setup');
    return result;
  }

  async start(): Promise<void> {
    if (!this.loaded) await this.prepare();
    this.startedAt = Date.now();
    this.pausedMs = 0;
    this.pausedAt = null;
    this.frameCount = 0;
    this.stateMachine.reset();
    this.smoother.reset();
    this.cvaMedian.reset();
    this.chaMedian.reset();
    this.trunkMedian.reset();
    this.protMedian.reset();
    this.featureWindow.clear();
    this.disagreements = 0;
    this.setPhase('running');
    await this.acquireWakeLock();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  pause(reason: PauseReason = 'user'): void {
    if (this.phase !== 'running') return;
    this.pauseReason = reason;
    this.pausedAt = Date.now();
    this.setPhase('paused', reason);
    void this.releaseWakeLock();
  }

  resume(): void {
    if (this.phase !== 'paused') return;
    if (this.pausedAt !== null) this.pausedMs += Date.now() - this.pausedAt;
    this.pausedAt = null;
    this.pauseReason = null;
    this.setPhase('running');
    void this.acquireWakeLock();
  }

  /**
   * Stop without telling anyone — for a recorder being replaced (camera flip).
   * Leaving the old one running doubled the detect calls on one model and one
   * video, which is what wore the GPU down on long setups.
   */
  dispose(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.stopLoop();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    void this.releaseWakeLock();
  }

  /** Re-subscribe to frames after the stream was swapped under the same <video>. */
  restartLoop(): void {
    if (this.stopped) return;
    this.stopLoop();
    this.lastCallbackAt = performance.now();
    this.startLoop();
  }

  /** Milliseconds since the video last delivered a frame to the loop. */
  msSinceLastCallback(now = performance.now()): number {
    return this.lastCallbackAt === 0 ? 0 : now - this.lastCallbackAt;
  }

  /** Stop sampling. Frames stay in IndexedDB for the report. */
  async stop(): Promise<void> {
    if (this.stopped) return;
    this.stopped = true;
    this.setPhase('ending');
    if (this.pausedAt !== null) {
      this.pausedMs += Date.now() - this.pausedAt;
      this.pausedAt = null;
    }
    this.stopLoop();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    await this.releaseWakeLock();
    this.setPhase('done');
  }

  // ── sampling loop ───────────────────────────────────────────────────────────

  private startLoop(): void {
    const video = this.options.video;
    const step = (): void => {
      this.lastCallbackAt = performance.now();
      this.sample();
      this.schedule(step);
    };
    if (typeof video.requestVideoFrameCallback === 'function') {
      this.videoFrameHandle = video.requestVideoFrameCallback(() => step());
    } else {
      this.rafHandle = requestAnimationFrame(() => step());
    }
  }

  private schedule(step: () => void): void {
    if (this.stopped) return;
    const video = this.options.video;
    if (typeof video.requestVideoFrameCallback === 'function') {
      this.videoFrameHandle = video.requestVideoFrameCallback(() => step());
    } else {
      // Safari < 18 and some Android WebViews: fall back to a timer at 2× fps so
      // the throttle below still lands on the configured sample rate.
      this.timerHandle = window.setTimeout(step, 1000 / (this.effectiveFps() * 2));
    }
  }

  private stopLoop(): void {
    const video = this.options.video;
    if (this.videoFrameHandle !== null && typeof video.cancelVideoFrameCallback === 'function') {
      video.cancelVideoFrameCallback(this.videoFrameHandle);
    }
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    if (this.timerHandle !== null) window.clearTimeout(this.timerHandle);
    this.videoFrameHandle = null;
    this.rafHandle = null;
    this.timerHandle = null;
  }

  /**
   * One fixed rate for the whole session, on every device (data consistency:
   * every session and check samples the same way). Set from Remote Config.
   * A phone that cannot keep up simply delivers fewer frames, and the
   * measured fps says so.
   */
  private effectiveFps(): number {
    return this.options.sampleFps;
  }

  private sample(): void {
    const { video } = this.options;
    if (!this.loaded || video.readyState < 2) return;

    const now = performance.now();
    const interval = 1000 / this.effectiveFps();
    if (now - this.lastSampleAt < interval - 1) return;
    this.lastSampleAt = now;
    if (this.lastFrameAt > 0) {
      const instant = 1000 / Math.max(1, now - this.lastFrameAt);
      this.measuredFps = this.measuredFps === 0 ? instant : this.measuredFps * 0.8 + instant * 0.2;
    }
    this.lastFrameAt = now;

    const aspect = video.videoWidth / Math.max(1, video.videoHeight);
    let result;
    try {
      result = detectOnVideo(this.loaded, video, now);
    } catch {
      void this.recoverTracking();
      return;
    }
    this.recoveries = 0;

    const poses = result.landmarks ?? [];
    const raw = (poses[0] ?? []) as Landmark[];
    const smoothed = raw.length > 0 ? this.smoother.smooth(raw, now) : raw;
    // Level by the phone's roll before anything is measured, so a phone
    // propped at a slant still reads true vertical (PHONE_TILT). The overlay
    // and the stored frames keep the picture's own coordinates.
    const roll = this.options.level?.() ?? null;
    const level = roll !== null && Math.abs(roll) <= PHONE_TILT.maxRollDeg ? roll : 0;
    const measured = level !== 0 && smoothed.length > 0 ? levelLandmarks(smoothed, aspect, level) : smoothed;

    const gate = qualityGate({
      landmarks: measured,
      poseCount: poses.length,
      aspect,
      meanLuma: meanLuma(video),
    });

    const metricsRaw = computeFrameMetrics({
      landmarks: measured,
      side: gate.side,
      aspect,
      hipAvailable: gate.flags.hipAvailable,
      kneeAvailable: gate.flags.kneeAvailable,
    });

    // Second smoothing stage: a 1 s median on the slow signals (ARCHITECTURE §1.2).
    const metrics: FrameMetrics = {
      ...metricsRaw,
      cva: metricsRaw.cva === null ? null : this.cvaMedian.push(metricsRaw.cva, now),
      cha: metricsRaw.cha === null ? null : this.chaMedian.push(metricsRaw.cha, now),
      trunkInclination:
        metricsRaw.trunkInclination === null
          ? null
          : this.trunkMedian.push(metricsRaw.trunkInclination, now),
      shoulderProtraction:
        metricsRaw.shoulderProtraction === null
          ? null
          : this.protMedian.push(metricsRaw.shoulderProtraction, now),
    };

    if (this.calibrating && gate.ok) this.calibrationSamples.push(metrics);

    const elapsedMs = this.elapsedMs;
    const ruleState = rawFrameState({
      metrics,
      gateOk: gate.ok,
      calibration: this.options.calibration ?? null,
    });

    // Artifact detector: reaching for a cup should not be recorded as a slouch.
    // It can only downgrade a frame to `invalid`, never upgrade one.
    this.featureWindow.push(elapsedMs, metrics, measured);
    if (this.classifier && now - this.lastClassifiedAt >= this.classifier.meta.inferenceIntervalMs) {
      this.lastClassifiedAt = now;
      void this.classifier.classify(this.featureWindow).then((verdict) => {
        this.lastVerdictIsArtifact = verdict?.isArtifact === true && verdict.confidence >= 0.7;
      });
    }
    const combined = applyClassifier(
      ruleState,
      this.lastVerdictIsArtifact ? { label: 'artifact', confidence: 1, isArtifact: true } : null,
    );
    if (combined.disagreement) this.disagreements += 1;

    const state =
      this.phase === 'running' ? this.stateMachine.push(combined.state, elapsedMs) : combined.state;

    // Gate failure tracking drives the 30 s banner and the auto-pause.
    if (gate.ok) {
      this.gateFailingSince = null;
    } else if (this.gateFailingSince === null) {
      this.gateFailingSince = now;
    }
    const gateFailingSec = this.gateFailingSince === null ? 0 : (now - this.gateFailingSince) / 1000;

    if (gate.ok && smoothed.length > 0) {
      this.framingSum += framingScore({ landmarks: measured, side: gate.side, aspect });
      this.framingSamples += 1;
    }

    if (this.phase === 'running') {
      // A second person invalidates the measurement outright: pause, do not guess.
      if (gate.reasons.includes('multiPerson')) {
        this.pause('multi_person');
      } else {
        this.frameCount += 1;
        const record: FrameRecord = {
          t: Math.round(elapsedMs),
          state,
          cva: metrics.cva,
          cha: metrics.cha,
          trunk: metrics.trunkInclination,
          prot: metrics.shoulderProtraction,
          hip: metrics.hipAngle,
          knee: metrics.kneeAngle,
          lm: quantize(smoothed),
        };
        void appendFrame(this.options.sessionId, record).catch(() => {
          this.options.onError({
            code: 'storage_full',
            message: 'Device storage is full, so this session cannot be saved.',
          });
        });
      }
    } else if (
      this.phase === 'paused' &&
      this.pauseReason !== null &&
      AUTO_RESUMING.has(this.pauseReason) &&
      gate.ok &&
      !gate.reasons.includes('multiPerson')
    ) {
      // Auto-resume once the interruption clears.
      this.resume();
    }

    this.options.onTick({
      phase: this.phase,
      elapsedMs,
      state,
      metrics,
      gate,
      landmarks: smoothed,
      fps: Math.round(this.measuredFps * 10) / 10,
      frameCount: this.frameCount,
      gateFailingSec,
      framing: this.framing,
    });
  }

  private setPhase(phase: RecorderPhase, reason?: PauseReason): void {
    this.phase = phase;
    this.options.onPhase(phase, reason);
  }

  /** iOS cannot keep the camera alive in the background, so pause and say so. */
  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') this.pause('background');
  };

  private async acquireWakeLock(): Promise<void> {
    const nav = navigator as Navigator & {
      wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> };
    };
    if (!nav.wakeLock) return;
    try {
      this.wakeLock = await nav.wakeLock.request('screen');
    } catch {
      // Denied or unsupported: the session still works, the screen may sleep.
      this.wakeLock = null;
    }
  }

  private async releaseWakeLock(): Promise<void> {
    try {
      await this.wakeLock?.release();
    } catch {
      // Already released.
    }
    this.wakeLock = null;
  }
}

/** Should the UI show the "can't measure" banner? (ARCHITECTURE §1.3) */
export function shouldShowGateBanner(gateFailingSec: number): boolean {
  return gateFailingSec >= THRESHOLDS.gate.failureBannerSec;
}

/** Thermal hint cadence for long sessions (ARCHITECTURE §3). */
export function shouldShowThermalHint(elapsedMs: number, shownCount: number): boolean {
  const everyMs = CAPTURE_DEFAULTS.thermalHintEveryMin * 60 * 1000;
  return elapsedMs >= (shownCount + 1) * everyMs;
}
